import { motion } from "framer-motion"
import { Link } from "gatsby"
import React, { useRef } from "react"
import Card from "react-bootstrap/Card"

import Button from "@knowit-alma/common/src/components/atoms/Button/Button"
import Image from "@knowit-alma/common/src/components/atoms/Image/Image"
import ImageCentered from "@knowit-alma/common/src/components/atoms/ImageCentered/ImageCentered"
import InlineLinkListItem from "@knowit-alma/common/src/components/atoms/InlineLinkListItem/InlineLinkListItem"
import Placeholder from "@knowit-alma/common/src/components/atoms/Placeholder/Placeholder"
import Tagline from "@knowit-alma/common/src/components/atoms/Tagline/Tagline"

import styles from "./Card.module.scss"

const ModifiedCard = ({
  headingSize = "h2",
  headingSmall,
  type = "regular",
  image,
  tagline,
  taglineSmall,
  title,
  imageSize = "regular",
  imageAspectRatio,
  imageCentered,
  imageCenteredSmall,
  url,
  internalLink,
  zoomImage = 1,
  placeholder = true,
  target = "_self",
  subtitle,
  list,
  fullAreaIsLink = true,
  cta,
  sublink,
}) => {
  const link = useRef()

  // Why proxylink?
  // "It's not disastrous in terms of comprehension, but verbose..."
  // (https://inclusive-components.design/cards/)

  const proxyLink = () => {
    link.current.click()
  }

  const hasLink = url ? "has-link" : false

  const camelCase = require("camelcase")

  const Heading = headingSize

  const [isHovered, setIsHovered] = React.useState(false)

  /* eslint-disable jsx-a11y/click-events-have-key-events */
  return (
    <Card
      className={`${styles.card} ${
        fullAreaIsLink ? styles.fullAreaIsLink : ""
      } ${styles[camelCase("" + type)]} ${styles[camelCase("" + hasLink)]}`}
      onClick={fullAreaIsLink ? proxyLink : undefined}
    >
      {hasLink && (
        <motion.div
          className={`${styles.image} ${styles[camelCase("" + imageSize)]}`}
          onHoverStart={() => setIsHovered(true)}
          onHoverEnd={() => setIsHovered(false)}
          initial={"default"}
          animate={isHovered ? "hover" : "default"}
        >
          <div
            style={{ transform: "scale(" + zoomImage + ")" }}
            onClick={!fullAreaIsLink ? proxyLink : undefined}
          >
            {image && !imageCentered && (
              <Image
                image={image}
                aspectRatio={imageAspectRatio}
                alt=""
                widths={[313, 612, 750, 1080]}
              />
            )}
            {image && imageCentered && (
              <ImageCentered
                aspectRatio={imageAspectRatio}
                small={imageCenteredSmall}
              >
                <Image image={image} alt="" widths={[313, 612, 750, 1080]} />
              </ImageCentered>
            )}
            {!image && placeholder && imageAspectRatio && (
              <Placeholder aspectRatio={imageAspectRatio} />
            )}
          </div>
        </motion.div>
      )}
      {!hasLink && (
        <div className={`${styles.image}`}>
          <div style={{ transform: "scale(" + zoomImage + ")" }}>
            {image && !imageCentered && (
              <Image
                image={image}
                aspectRatio={imageAspectRatio}
                alt=""
                widths={[313, 612, 750, 1080]}
              />
            )}
            {image && imageCentered && (
              <ImageCentered
                aspectRatio={imageAspectRatio}
                small={imageCenteredSmall}
              >
                <Image image={image} alt="" widths={[313, 612, 750, 1080]} />
              </ImageCentered>
            )}
            {!image && placeholder && imageAspectRatio && (
              <Placeholder aspectRatio={imageAspectRatio} />
            )}
          </div>
        </div>
      )}
      <div className={styles.content}>
        {tagline && (
          <Tagline size={taglineSmall ? "small" : ""}>{tagline}</Tagline>
        )}
        <Heading className={headingSmall ? styles.headingSmall : ""}>
          {!url && <>{title}</>}
          {url && internalLink && (
            <Link to={url} ref={link}>
              {title}
            </Link>
          )}
          {url && !internalLink && (
            <a
              href={url}
              ref={link}
              target={target}
              rel={target === "_blank" ? "noopener noreferrer" : null}
            >
              {title}
            </a>
          )}
        </Heading>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        {list && (
          <ul className={styles.list}>
            {list
              .map((item, index) => {
                return item ? <li key={index}>{item}</li> : null
              })
              .filter(Boolean)}
          </ul>
        )}
        {cta && url && (
          <div className="base-margin-top-xxxs">
            <Button
              variant="secondary"
              size="small"
              onClick={!fullAreaIsLink ? proxyLink : undefined}
            >
              {cta}
            </Button>
          </div>
        )}
        {sublink && (
          <div className="base-margin-top-xxs">
            <InlineLinkListItem
              href={sublink.url}
              text={sublink.title}
              internal={sublink.target !== "_blank"}
              element="p"
            />
          </div>
        )}
      </div>
    </Card>
  )
  /* eslint-enable */
}

export default ModifiedCard
